import type { FC } from 'react';

import Refresh from '@crehana/compass.icons.outline/Refresh';
import Slash from '@crehana/compass.icons.outline/Slash';
import { LoadingButton, useUITheme } from '@crehana/compass.ui';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Dialog, DialogContent, type DialogProps } from '@crehana/ui';

import { useDeactivateRoute } from './hooks';
import { LOCALES } from './locales';

interface ExitDialogProps extends DialogProps {
  isActivating?: boolean;
  teamId: number;
  onComplete?: () => void;
}

const UpdateRequirements: FC<React.PropsWithChildren<ExitDialogProps>> = ({
  open,
  isActivating,
  teamId,
  onDismiss,
  onComplete,
}) => {
  const { isDark } = useUITheme();
  const { languageCode } = useTranslation();
  const T_KEY = LOCALES[languageCode as keyof typeof LOCALES];

  const { onDeactivate, loading } = useDeactivateRoute({ onComplete });

  const title = isActivating ? T_KEY.TITLE_2 : T_KEY.TITLE;
  const submit = isActivating ? T_KEY.ACTIVATE : T_KEY.DEACTIVATE;

  return (
    <>
      <Dialog
        size="s"
        open={open}
        isDark={isDark}
        onDismiss={onDismiss}
        ignoreBackdropClick
      >
        <DialogContent
          type="m"
          isDark={isDark}
          mainTitle={
            <div className="tw-flex tw-flex-col tw-align-center">
              <div className="tw-flex tw-items-center tw-justify-center tw-pb-1.2">
                {isActivating ? (
                  <div className="tw-flex tw-justify-center tw-items-center tw-bg-success-100 tw-w-5.6 tw-h-5.6 tw-rounded-full">
                    <div className="tw-flex tw-justify-center tw-items-center tw-bg-success-300 tw-w-4.8 tw-h-4.8 tw-rounded-full">
                      <Refresh
                        size={28}
                        className="tw-text-neutral-light-100"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="tw-flex tw-justify-center tw-items-center tw-bg-neutral-light-300 tw-w-5.6 tw-h-5.6 tw-rounded-full">
                    <div className="tw-flex tw-justify-center tw-items-center tw-bg-neutral-light-400 tw-w-4.8 tw-h-4.8 tw-rounded-full">
                      <Slash size={28} className="tw-text-neutral-light-700" />
                    </div>
                  </div>
                )}
              </div>
              <p className="font-h4 whitespace-pre-line">{title}</p>
            </div>
          }
          body={
            <div className="text-left">
              <p className="font-subtitle4">{T_KEY.SUBTITLE}</p>
              <ul className="font-body4">
                {isActivating ? (
                  <>
                    <li className="tw-list-disc">{T_KEY.LIST_2_1}</li>
                    <li className="tw-list-disc">{T_KEY.LIST_2_2}</li>
                  </>
                ) : (
                  <>
                    <li className="tw-list-disc">{T_KEY.LIST_1}</li>
                    <li className="tw-list-disc">{T_KEY.LIST_2}</li>
                    <li className="tw-list-disc">{T_KEY.LIST_3}</li>
                    <li className="tw-list-disc">{T_KEY.LIST_4}</li>
                  </>
                )}
              </ul>
            </div>
          }
          cancelAction={
            // TODO: talk with the code owner to fix this code so we can enable this rule again
            // eslint-disable-next-line react/button-has-type
            // biome-ignore lint/a11y/useButtonType: button type handled elsewhere
            <button className="tw-btn-tertiary-s" onClick={onDismiss}>
              {T_KEY.CANCEL}
            </button>
          }
          confirmAction={
            loading ? (
              <LoadingButton
                size="s"
                isWhite={false}
                type={isActivating ? 'primary' : 'deactivate'}
              />
            ) : (
              // TODO: talk with the code owner to fix this code so we can enable this rule again
              // eslint-disable-next-line react/button-has-type
              // biome-ignore lint/a11y/useButtonType: button type handled elsewhere
              <button
                className={
                  isActivating ? 'tw-btn-primary-s' : 'tw-btn-deactivate-s'
                }
                onClick={() =>
                  onDeactivate({ teamId, isHidden: !isActivating })
                }
              >
                {submit}
              </button>
            )
          }
        />
      </Dialog>
    </>
  );
};

export default UpdateRequirements;
